import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import ManualBonusService from '../../services/manualBonus.service';
import { showLoading, hideLoading } from '../../lib/uiService';
import useErrorHandling from '../../hooks/useErrorHandling';
import { formatMonthBitmask } from '../../utils/formatBonus';
import CustomTable from '../../components/common/Table';
import Pagination from '../../components/common/Pagination';
import CustomSelect from '../../components/common/CustomSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrashCan, faPlus, faGift, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import { upcaseFirst } from '../../utils/formatBonus';

// Map for badge colors by periodType
const periodTypeBadgeColor: Record<string, string> = {
  month: 'primary',
  quarter: 'success',
  halfyear: 'warning',
  year: 'info',
};

const ManualBonusList: React.FC = () => {
  const [bonuses, setBonuses] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const pageOptions = [
    { label: 'All', value: Number.MAX_SAFE_INTEGER },
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
  ];
  const [rowPerPage, setRowPerPage] = useState(pageOptions[0].value);
  const navigate = useNavigate();
  const { setError } = useErrorHandling();

  const hadlePageToShow = (value: any) => {
    setRowPerPage(Number(value));
  };

  const onCreateBonus = () => {
    navigate(`/manual-bonus/create`);
  };

  const onEditBonus = (manualBonusId: string) => {
    navigate(`/manual-bonus/${manualBonusId}`);
  };

  const onNotifyEmailBonus = (manualBonusId: string) => {
    ManualBonusService.SendEmailNotificationBonus([manualBonusId])
      .then(() => {
        toast.success('Email notification sent successfully');
      })
      .catch((error) => {
        console.log(error);
        toast.error('Failed to send email notification');
      });
  };

  const columns: any = useMemo(
    () => [
      {
        Header: 'Period',
        id: 'period',
        accessor: 'period',
        canSort: true,
        Cell: ({ data, row }: { data: any; row: any }) => {
          const { periodType, year, monthBitmask, manualBonusId } = data[row.id];
          const badgeColor = periodTypeBadgeColor[periodType] || 'secondary';
          return (
            <td>
              <Link to={`/manual-bonus/${manualBonusId}`} style={{ textDecoration: 'none' }}>
                <Badge bg={badgeColor} style={{ minWidth: 80, textAlign: 'center' }}>
                  {upcaseFirst(periodType)}
                </Badge>
                {` ${year}-${formatMonthBitmask(Number(monthBitmask), periodType)}`}
              </Link>
            </td>
          );
        },
      },
      {
        Header: 'Total bonus',
        id: 'amount',
        accessor: 'amount',
        canSort: true,
        Cell: ({ data, row }: { data: any; row: any }) => {
          const bonus = data[row.id];
          const amount =
            bonus?.manualBonusUsers?.reduce(
              (sum: number, userBonus: any) => sum + Number(userBonus.bonusAmount || 0),
              0,
            ) || 0;
          return <td>${amount}</td>;
        },
      },
      {
        Header: 'Users',
        id: 'users',
        accessor: 'manualBonusUsers',
        canSort: true,
        Cell: ({ data, row }: { data: any; row: any }) => {
          const bonus = data[row.id];
          const userCount = bonus?.manualBonusUsers?.reduce((count: number, userBonus: any) => {
            return count + (userBonus.isActive !== false ? 1 : 0);
          }, 0);
          return <td>{userCount}</td>;
        },
      },
      {
        Header: 'Created By',
        id: 'createdBy',
        accessor: 'createdByUser',
        canSort: false,
        Cell: ({ data, row }: { data: any; row: any }) => {
          const { createdByUser } = data[row.id];
          return <td>{[createdByUser?.firstName, createdByUser?.lastName].filter(Boolean).join(' ')}</td>;
        },
      },
      {
        Header: 'Date',
        id: 'submission',
        accessor: 'submissionDate',
        canSort: false,
        Cell: ({ data, row }: { data: any; row: any }) => {
          const { submissionDate } = data[row.id];
          return <td>{submissionDate ? new Date(submissionDate).toLocaleDateString() : ''}</td>;
        },
      },
      {
        Header: 'Description',
        id: 'description',
        accessor: 'description',
        canSort: false,
        Cell: ({ data, row }: { data: any; row: any }) => {
          const { description } = data[row.id];
          return <td>{description}</td>;
        },
      },
      {
        Header: '',
        id: 'action',
        canSort: false,
        Cell: ({ data, row }: { data: any; row: any }) => {
          const { manualBonusId } = data[row.id];
          return (
            <td>
              <OverlayTrigger placement={'top'} overlay={<Tooltip>Send email notification.</Tooltip>}>
                <button
                  className="btn btn-primary btn-sm me-1"
                  type="button"
                  onClick={() => onNotifyEmailBonus(manualBonusId)}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </button>
              </OverlayTrigger>
              <button className="btn btn-primary btn-sm me-1" type="button" onClick={() => onEditBonus(manualBonusId)}>
                <FontAwesomeIcon icon={faPenToSquare} />
              </button>
              <button className="btn btn-danger btn-sm" type="button" onClick={() => onDeleteBonus(manualBonusId)}>
                <FontAwesomeIcon icon={faTrashCan} />
              </button>
            </td>
          );
        },
      },
    ],
    [],
  );

  const onDeleteBonus = async (manualBonusId: string) => {
    // Handle delete bonus logic here
    showLoading();
    try {
      await ManualBonusService.DeleteManualBonus(manualBonusId);
      hideLoading();
      await toast.success('Successfully Deleted Bonuses');
      fetchList(page, search, rowPerPage);
    } catch (error) {
      hideLoading();
      console.log(error);
    }
  };

  useEffect(() => {
    fetchList(1, '', rowPerPage);
  }, []);

  useEffect(() => {
    fetchList(page, search, rowPerPage);
  }, [page, rowPerPage]);

  const fetchList = (page: number, searchString?: string, pageSize?: any) => {
    showLoading();
    ManualBonusService.GetManualBonuses({
      page,
      pageSize,
      search: searchString,
    })
      .then(({ data }) => {
        setBonuses(data.manualBonuses);
        setTotalCount(data.totalCount);
        setPageCount(Math.ceil(data.totalCount / rowPerPage));
        hideLoading();
      })
      .catch((error) => {
        hideLoading();
        setError(error.status);
      });
  };

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value);
      if (pageCount < 2) {
        return;
      }
      if (value.length === 0 || value.length > 2) {
        fetchList(page, value, rowPerPage);
      }
    },
    [pageCount, search],
  );

  return (
    <>
      <h3 className="text-dark mb-4">Manual Bonuses</h3>
      <Card>
        <div className="p-3">
          <h5 className="text-primary fw-bold m-0 pb-1 pt-1 d-flex align-items-center ">
            <FontAwesomeIcon size="2x" icon={faGift} />
            <span className="mx-2">Current New Bonuses</span>
          </h5>
        </div>
        <div className="row p-3">
          <div className="col-md-6 text-nowrap d-flex">
            <div className="mb-2">
              <label className="col-form-label" htmlFor="showperpage">
                Show:&nbsp;
              </label>
              <CustomSelect
                defaultValue={'All'}
                options={pageOptions}
                onSelectOption={hadlePageToShow}
                setPage={setPage}
              />
            </div>
            <div className="mb-2 mx-5">
              <button
                className="btn btn-primary float-end d-flex align-items-center"
                type="button"
                onClick={onCreateBonus}
              >
                <FontAwesomeIcon className="text-white" icon={faPlus} />
                &nbsp;Create Manual Bonus
              </button>
            </div>
          </div>
          <div className="col-md-6">
            <div className="text-md-end dataTables_filter">
              <label className="form-label">
                <input
                  type="search"
                  className="form-control form-control-sm"
                  aria-controls="dataTable"
                  placeholder="Search"
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </label>
            </div>
          </div>
        </div>
        <div className="p-3">
          <CustomTable columns={columns} data={bonuses} search={search} />
        </div>
        {bonuses.length > 0 && (
          <div className="row px-3">
            <div className="col-md-6">
              <p id="dataTable_info-3" className="dataTables_info" role="status" aria-live="polite">
                Showing {bonuses.length} of {totalCount}
              </p>
            </div>
            <div className="col-md-6">
              <Pagination total={pageCount} currentPage={page} setPage={setPage} />
            </div>
          </div>
        )}
      </Card>
    </>
  );
};

export default ManualBonusList;
